import React, { useEffect, useState, useMemo } from "react";
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Fade,
  Slide,
  Grow,
  Snackbar,
  Autocomplete,
  TextField,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Collapse,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { SchoolData, useSchools } from "../hooks/useSchools";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useMedium } from "../components/layout/Layout";

import { useSchoolIdForAdmin } from "../hooks/useSchoolIdForAdmin";
import { useSessionClass } from "../contexts/SessionClassContext";
import { useStudents } from "../hooks/useStudents";
import { useQueryClient } from "@tanstack/react-query";
import SchoolIcon from "@mui/icons-material/School";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import CodeIcon from "@mui/icons-material/Code";
import PublicIcon from "@mui/icons-material/Public";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import LanguageIcon from "@mui/icons-material/Language";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

// Dummy data for username/password table
const userPassRows = Array.from({ length: 10 }, (_, i) => ({
  username: "",
  password: "",
}));

// importantLinks moved inside component to support translations

const linkButtonStyle = {
  mb: 1.5,
  width: "100%",
  textTransform: "none",
  fontWeight: "bold",
  fontSize: 14,
  bgcolor: "rgba(255, 255, 255, 0.9)",
  color: "#3092e3",
  border: "2px solid rgba(25, 118, 210, 0.3)",
  borderRadius: 3,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  whiteSpace: "normal",
  wordBreak: "break-all",
  overflowWrap: "anywhere",
  maxWidth: "100%",
  backdropFilter: "blur(10px)",
  "&:hover": {
    bgcolor: "#1976d2",
    color: "#fff",
    boxShadow: "0 8px 25px rgba(25, 118, 210, 0.3)",
    border: "2px solid #3092e3",
    transform: "translateY(-2px) scale(1.02)",
  },
};

const sectionTitleStyle = {
  fontWeight: "bold",
  fontSize: 22,
  mb: 3,
  color: "#fff",
  letterSpacing: 1,
  textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
  display: "flex",
  alignItems: "center",
  gap: 1,
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [data, setData] = useState<SchoolData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const apiBaseUrl = process.env.REACT_APP_API_URL;
  const { user, isLoading: authLoading } = useAuth();
  // Check if user has APP_USER role
  const isAppUser = user?.roles?.includes('APP_USER');
  const isStudent = user?.roles?.includes('STUDENT');
  const { useSchoolById } = useSchools(false, { enableQuery: false });
  const { selectedSession } = useSessionClass();
  const { selectedMedium } = useMedium();
  // Disable API call for ADMIN/APP_USER/SCHOOL_ADMIN/TEACHER/STUDENT users - they already have schoolId from user context
  const { data: adminSchoolId, isLoading: schoolIdLoading } = useSchoolIdForAdmin(
    isAppUser || user?.roles?.includes('ADMIN') || user?.roles?.includes('SCHOOL_ADMIN') || user?.roles?.includes('TEACHER') || isStudent ? undefined : user?.id
  );

  // For both teachers and school admins, use their schoolId directly from user context
  // This ensures we use the actual schoolId from the logged-in user
  const schoolId = user?.schoolId;

  const { data: school, isLoading: schoolLoading } = useSchoolById(schoolId ?? undefined);
  // Disable students query for APP_USER - they don't need to fetch all students
  const { studentsQuery } = useStudents(false, schoolId ?? undefined, !isStudent && !isAppUser);

  // Initialize mediums from school data
  useEffect(() => {
    if (school?.medium) {
      setMediums(Array.isArray(school.medium) ? school.medium : [school.medium]);
    } else {
      setMediums([]);
    }
  }, [school?.medium]);

  // Use selectedSession from context for display (no API call needed on dashboard)
  const displaySession = selectedSession;

  // Refetch students when component mounts and schoolId is available (skip for APP_USER)
  useEffect(() => {
    if (schoolId && !isStudent && !isAppUser) {
      studentsQuery.refetch();
    }
  }, [schoolId, isStudent, isAppUser]);

  // Calculate total students count (return "-" for APP_USER since they don't need this data)
  const totalStudentsCount = useMemo(() => {
    if (isAppUser) return "-";
    if (!studentsQuery.data) return "-";
    return studentsQuery.data.length;
  }, [studentsQuery.data, isAppUser]);

  const [openMenu, setOpenMenu] = useState<number | null>(null);

  const handleMenuClick = (idx: number) => {
    setOpenMenu(openMenu === idx ? null : idx);
  };

  // Image upload state and handlers (only for SCHOOL_ADMIN users)
  const { user: authUser } = useAuth();
  const { updateSchoolMutation } = useSchools(false, { enableQuery: false });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [schoolImageFile, setSchoolImageFile] = useState<File | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [mediums, setMediums] = useState<string[]>([]);
  const [newMedium, setNewMedium] = useState<string>("");
  const [mediumDialogOpen, setMediumDialogOpen] = useState(false);
  const [imageVersion, setImageVersion] = useState(0);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setImageFile(file);
    } else {
      setImageFile(null);
    }
  };

  const handleSchoolImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setSchoolImageFile(file);
    } else {
      setSchoolImageFile(null);
    }
  };

  const handleUpdateImages = (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (school && (imageFile || schoolImageFile)) {
      const updatedSchoolData = {
        ...school,
        schoolLogo: imageFile ? imageFile.name : school.schoolLogo,
        schoolImage: schoolImageFile ? schoolImageFile.name : school.schoolImage,
      };

      updateSchoolMutation.mutate({
        id: school.id!,
        schoolData: updatedSchoolData,
        imageFile: imageFile || undefined,
        schoolImageFile: schoolImageFile || undefined,
      }, {
        onSuccess: () => {
          setImageFile(null);
          setSchoolImageFile(null);
          setShowSuccessMessage(true);
          
          // Invalidate queries to update images without page refresh
          if (schoolId) {
            queryClient.invalidateQueries({ queryKey: ["schoolById", schoolId] });
            queryClient.invalidateQueries({ queryKey: ["schoolLogoInfo", schoolId] });
            queryClient.invalidateQueries({ queryKey: ["schoolImageInfo", schoolId] });
            // Increment image version to force cache refresh
            setImageVersion(prev => prev + 1);
          }
        },
        onError: () => {
          setShowErrorMessage(true);
        }
      });
    }
  };

  const handleAddMedium = () => {
    if (newMedium.trim() && !mediums.includes(newMedium.trim().toUpperCase())) {
      const mediumToAdd = newMedium.trim().toUpperCase();
      setMediums([...mediums, mediumToAdd]);
      setNewMedium("");
    }
  };

  const handleRemoveMedium = (mediumToRemove: string) => {
    setMediums(mediums.filter(m => m !== mediumToRemove));
  };

  const handleUpdateMediums = () => {
    if (school) {
      const updatedSchoolData = {
        ...school,
        medium: mediums,
      };

      updateSchoolMutation.mutate({
        id: school.id!,
        schoolData: updatedSchoolData,
      }, {
        onSuccess: () => {
          setShowSuccessMessage(true);
          // Auto-reload the page after successful medium update
          setTimeout(() => {
            window.location.reload();
          }, 1500);
        },
        onError: () => {
          setShowErrorMessage(true);
        }
      });
    }
  };

  const hasImageChanges = imageFile || schoolImageFile;
  const hasMediumChanges = JSON.stringify(mediums.sort()) !== JSON.stringify((school?.medium || []).sort());
  const isSchoolAdmin = authUser?.roles?.includes("SCHOOL_ADMIN");
  const isTeacher = authUser?.roles?.includes("TEACHER");
  const canManageImages = isSchoolAdmin;

  // Translated Important Links - Different links for students vs other users
  const importantLinks = useMemo(
    () => {
      // If user is a student, show student-specific links
      if (isStudent) {
        return [
          {
            label: t("exam_links") || "Exam Links",
            color: "linear-gradient(135deg, #0168EA 0%, #3790ff 100%)",
            icon: "🎓",
            links: [
              {
                text: t("link_mp_board_results"),
                url: "https://result.mponline.gov.in/Result/ResultSecondExam?rspbmhsh=MgfgfJHGFFtytytykoReiLme7klyzA1kUfdsfdsfDDSFDSFDsdai4eyrZZGnPzi9K1pc7sKI0vs==",
              },
              {
                text: t("link_retotaling_answerbook"),
                url: "https://mpbse.mponline.gov.in/ras/ras/rasstatus",
              },
              {
                text: t("link_jee"),
                url: "https://jeeadv.ac.in/imp_dates.html"
              },
              {
                text: t("link_neet"),
                url: "https://neet.careerguide.com/registration/#13"
              },
            ],
          },
          {
            label: t("scholarship_links") || "Scholarship Links",
            color: "linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)",
            icon: "🎓",
            links: [
              {
                text: t("link_mptaasc"),
                url: "https://www.tribal.mp.gov.in/MPTAAS",
              },
              {
                text: t("link_nsp"),
                url: "https://scholarships.gov.in/otrapplication/#/",
              },
            ],
          },
          {
            label: t("certificate_links") || "Certificate Links",
            color: "linear-gradient(135deg, #ff9800 0%, #ffc107 100%)",
            icon: "📄",
            links: [
              {
                text: t("link_cast_certificate"),
                url: "https://www.mpedistrict.gov.in/MPL/ShowServiceDetail.aspx?param=tRzkQMyq7hexCSoHBRxcVqsPT+tO5xhGU7mvoavrIpBbUJq4uNmlNQH218UlhJ/vaHjgiM5gJ3k=",
              },
              {
                text: t("link_income_certificate"),
                url: "https://www.mpedistrict.gov.in/MPL/ServiceRegistrationStatus.aspx?by=RegNo",
              },
            ],
          },
          {
            label: t("govt_bodies_links") || "Government and Regulatory Bodies",
            color: "linear-gradient(135deg, #9c27b0 0%, #e040fb 100%)",
            icon: "🏛️",
            links: [
              {
                text: t("link_npci"),
                url: "https://www.npci.org.in/",
              },
              {
                text: t("link_mp_employee_selection_board"),
                url: "https://esb.mp.gov.in/Hindi/h_default.html",
              },
              {
                text: t("link_staff_selection_commission"),
                url: "https://ssc.gov.in/home"
              },
              {
                text: t("link_union_public_service_commission"),
                url: "https://upsc.gov.in/"
              },
            ],
          },
          {
            label: t("other_resources_links") || "Other Resources",
            color: "linear-gradient(135deg, #03a9f4 0%, #00bcd4 100%)",
            icon: "🌐",
            links: [
              {
                text: t("link_diksha_portal"),
                url: "https://pmevidya.education.gov.in/diksha.html"
              },
              {
                text: t("link_download_aadhaar"),
                url: "https://myaadhaar.uidai.gov.in/genricDownloadAadhaar/en",
              }
            ],
          },
        ];
      }
      
      // For non-students, show the original links
      return [
        {
          label: t("education_portal"),
          color: "linear-gradient(135deg, #0168EA 0%, #3790ff 100%)",
          icon: "🎓",
          links: [
            {
              text: "https://educationportal.mp.gov.in/",
              url: "https://educationportal.mp.gov.in/",
            },
            {
              text: "https://educationportal.mp.gov.in/sLogin.aspx",
              url: "https://educationportal.mp.gov.in/sLogin.aspx",
            },
            {
              text: "https://sederp.educationportal3.in/",
              url: "https://sederp.educationportal3.in/",
            },
            {
              text: "https://www.educationportal.mp.gov.in/ESB/Public/ESB_Main.aspx",
              url: "https://www.educationportal.mp.gov.in/ESB/Public/ESB_Main.aspx",
            },
          ],
        },
        {
          label: t("board_of_secondary_education"),
          color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
          icon: "📚",
          links: [
            {
              text: "https://mpbse.mponline.gov.in/MPBSE/MPBSE",
              url: "https://mpbse.mponline.gov.in/MPBSE/MPBSE",
              red: true,
            },
            {
              text: "https://mpbse.mponline.gov.in/Login",
              url: "https://mpbse.mponline.gov.in/Login",
              red: true,
            },
          ],
        },
        {
          label: t("vimarsh_portal"),
          color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
          icon: "🌐",
          links: [
            {
              text: "https://www.vimarsh.mp.gov.in/(S(2mvfyindj3gm1bezhmftjnxl))/school/index.aspx",
              url: "https://www.vimarsh.mp.gov.in/(S(2mvfyindj3gm1bezhmftjnxl))/school/index.aspx",
            },
          ],
        },
      ];
    },
    [t, isStudent]
  );

  // Update the schoolImageUrl section - will update when school data or image version changes
const schoolImageUrl = useMemo(() => {
  if (school && school.schoolImage) {
    // Add version parameter for cache busting when image is updated
    return `${apiBaseUrl}/schools/school-image/${school.schoolImage}?v=${imageVersion}`;
  }
  return `/3010ca78-6194-45ee-a965-c6550ad0e444.jpg`;
}, [apiBaseUrl, school?.schoolImage, imageVersion]);



  // School data is now handled by the useSchoolById hook
  useEffect(() => {
    if (school) {
      setData(school);
      setLoading(false);
    } else if (!schoolLoading) {
      setLoading(false);
    }
  }, [school, schoolLoading]);

  
  // For students and APP_USER, don't wait for studentsQuery since they don't need that data
  // Only wait for school data which is already being fetched via useSchoolById
  if (
    authLoading ||
    schoolLoading ||
    (!isStudent && !isAppUser && studentsQuery.isLoading)
  )
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #0168EA 0%, #3790ff 100%)",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#fff" }} />
      </Box>
    );
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        overflow: "hidden",
      }}
    >
      {/* School image as full background */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
          background: `url(${schoolImageUrl}) center/cover no-repeat`,
          opacity: 1,
        }}
      />
      {/* HERO SECTION */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: 1000,
          mx: "auto",
          pt: { xs: 5, md: 8 },
          pb: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >

        {/* School Name */}
        <Typography
          variant="h2"
          sx={{
            background: "linear-gradient(135deg, #ffffff 0%, #f0f8ff 100%)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color:"white",
            // WebkitTextFillColor: "transparent",
            fontWeight: "bold",
            textShadow: "0 4px 20px rgba(0,0,0,0.3)",
            mb: 2,
            letterSpacing: 1.5,
            fontSize: { xs: "2.5rem", md: "4rem" },
            textAlign: "center",
            lineHeight: 1.1,
            filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
            animation: "fadeInUp 1s ease-out",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(30px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          {data?.nameofSchool || t("school_name")}
        </Typography>
        {/* School Address */}
        <Typography
          variant="h6"
          sx={{
            color: "rgba(255,255,255,0.95)",
            textShadow: "0 2px 8px rgba(0,0,0,0.5)",
            mb: 3,
            fontWeight: 500,
            fontSize: { xs: "1.2rem", md: "1.6rem" },
            textAlign: "center",
            letterSpacing: 0.5,
            animation: "fadeInUp 1s ease-out 0.2s both",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(20px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
          <LocationOnIcon sx={{ color: "#FFD700", fontSize: "1.5rem" }} />
          {data?.district && data?.state 
            ? `${data.district}, ${data.state}` 
            : t("school_address")
          }
        </Typography>
      </Box>

      {/* STATS - enhanced with modern design - Hidden for APP_USER and Students */}
      {!isAppUser && !isStudent && (
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            width: "100%",
            maxWidth: selectedMedium === "ALL" ? 1200 : 900,
            mx: "auto",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "center",
            alignItems: "center",
            gap: 3,
            mb: 4,
            animation: "fadeInUp 1s ease-out 0.4s both",
            "@keyframes fadeInUp": {
              "0%": {
                opacity: 0,
                transform: "translateY(30px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          }}
        >
        <Box
          sx={{
            flex: 1,
            minWidth: 160,
            background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.95) 100%)",
            borderRadius: 3,
            py: 2.5,
            px: 1,
            boxShadow: "0 8px 32px rgba(1, 104, 234, 0.15)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.3)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: "0 12px 40px rgba(1, 104, 234,0.2)",
            },
          }}
        >
          <PersonIcon sx={{ color: "#3092e3", fontSize: 36, mb: 1 }} />
          <Typography
            sx={{
              color: "#333",
              fontWeight: 700,
              fontSize: 16,
              mb: 0.5,
              textAlign: "center",
            }}
          >
            {t("total_students")}
          </Typography>
          <Typography
            sx={{
              color: "#3092e3",
              fontWeight: 700,
              fontSize: 24,
              textAlign: "center",
            }}
          >
            {totalStudentsCount}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 160,
            background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.95) 100%)",
            borderRadius: 3,
            py: 2.5,
            px: 1,
            boxShadow: "0 8px 32px rgba(1, 104, 234, 0.15)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.3)",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: "0 12px 40px rgba(1, 104, 234, 0.2)",
            },
          }}
        >
          <CalendarTodayIcon sx={{ color: "#3092e3", fontSize: 36, mb: 1 }} />
          <Typography
            sx={{
              color: "#333",
              fontWeight: 700,
              fontSize: 16,
              mb: 0.5,
              textAlign: "center",
            }}
          >
            {t("active_session")}
          </Typography>
          <Typography
            sx={{
              color: "#3092e3",
              fontWeight: 700,
              fontSize: 24,
              textAlign: "center",
            }}
          >
            {displaySession?.name || "-"}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: selectedMedium === "ALL" ? "0 0 auto" : 1,
            width: selectedMedium === "ALL" ? 500 : undefined,
            minWidth: selectedMedium === "ALL" ? undefined : 160,
            background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.95) 100%)",
            borderRadius: 3,
            py: 2.5,
            px: 1,
            boxShadow: "0 8px 32px rgba(1, 104, 234, 0.15)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255,255,255,0.3)",
            transition: "all 0.3s ease",
            position: "relative",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: "0 12px 40px rgba(1, 104, 234, 0.2)",
            },
          }}
        >
          {isSchoolAdmin && (
            <IconButton
              onClick={() => setMediumDialogOpen(true)}
              sx={{
                position: "absolute",
                bottom: 8,
                right: 8,
                color: "#3092e3",
                "&:hover": {
                  backgroundColor: "rgba(48, 146, 227, 0.1)",
                },
              }}
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          )}
          <PublicIcon sx={{ color: "#3092e3", fontSize: 36, mb: 1 }} />
          <Typography
            sx={{
              color: "#333",
              fontWeight: 700,
              fontSize: 16,
              mb: 0.5,
              textAlign: "center",
            }}
          >
            {t("medium")}
          </Typography>
          <Typography
            sx={{
              color: "#3092e3",
              fontWeight: 700,
              fontSize: selectedMedium === "ALL" ? 20 : 24,
              textAlign: "center",
              lineHeight: selectedMedium === "ALL" ? 1.3 : 1.2,
              wordWrap: "break-word",
              whiteSpace: "normal",
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            {selectedMedium === "ALL" 
              ? (Array.isArray(data?.medium) ? data.medium.join(', ') : data?.medium)
              : selectedMedium
            }
          </Typography>
        </Box>
      </Box>
      )}

      {/* MAIN CONTENT: School Details (left) and Important Links (right) */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 4,
          alignItems: "stretch",
          justifyContent: "center",
        }}
      >
        {/* School Details - left - Hidden for Students */}
        {!isStudent && (
        <Box
          sx={{
            flex: 1,
            minWidth: { xs: "100%", md: 400 },
            maxWidth: { xs: "100%", md: "50%" },
            background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.95) 100%)",
            borderRadius: 4,
            p: { xs: 3, md: 4 },
            mb: { xs: 3, md: 0 },
            boxShadow: "0 8px 32px rgba(1, 104, 234, 0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            display: "flex",
            flexDirection: "column",
            animation: "fadeInLeft 1s ease-out 0.6s both",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: "0 12px 40px rgba(1, 104, 234, 0.2)",
            },
            transition: "all 0.3s ease",
            "@keyframes fadeInLeft": {
              "0%": {
                opacity: 0,
                transform: "translateX(-30px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateX(0)",
              },
            },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "#333",
              fontWeight: "bold",
              mb: 3,
              fontSize: { xs: "1.4rem", md: "1.8rem" },
              letterSpacing: 0.5,
              textAlign: "center",
            }}
          >
            🏫 {t("school_details")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 2.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CodeIcon sx={{ color: "#3092e3" }} />
              <Typography
                sx={{
                  color: "#333",
                  fontWeight: 600,
                  minWidth: 110,
                }}
              >
                {t("school_code")}:
              </Typography>
              <Typography
                sx={{
                  color: "#555",
                  fontWeight: 400,
                }}
              >
                {data?.schoolCode}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PublicIcon sx={{ color: "#3092e3" }} />
              <Typography
                sx={{
                  color: "#333",
                  fontWeight: 600,
                  minWidth: 110,
                }}
              >
                {t("district")}:
              </Typography>
              <Typography
                sx={{
                  color: "#555",
                  fontWeight: 400,
                }}
              >
                {data?.district}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PersonIcon sx={{ color: "#3092e3" }} />
              <Typography
                sx={{
                  color: "#333",
                  fontWeight: 600,
                  minWidth: 110,
                }}
              >
                {t("principal")}:
              </Typography>
              <Typography
                sx={{
                  color: "#555",
                  fontWeight: 400,
                }}
              >
                {data?.schoolPrincipalName}
              </Typography>
            </Box>
                         <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
               <PhoneIcon sx={{ color: "#3092e3" }} />
               <Typography
                 sx={{
                   color: "#333",
                   fontWeight: 600,
                   minWidth: 110,
                 }}
               >
                 {t("contact")}:
               </Typography>
               <Typography
                 sx={{
                   color: "#555",
                   fontWeight: 400,
                 }}
               >
                 {data?.principalMobile}
               </Typography>
             </Box>

             {/* Image Management Section - for SCHOOL_ADMIN and TEACHER users */}
             {canManageImages && school && (
               <>
                 <Box sx={{ 
                   borderTop: "1px solid rgba(48, 146, 227, 0.2)", 
                   pt: 2, 
                   mt: 2 
                 }}>
                   
                   <Grid container spacing={2} sx={{ mb: 2 }}>
                     {/* School Logo */}
                     <Grid item xs={12} md={6}>
                       <Box
                         sx={{
                           p: 1.5,
                           height: '70px',
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'center',
                           justifyContent: 'center',
                           gap: 0.5,
                           bgcolor: 'rgba(48, 146, 227, 0.05)',
                           borderRadius: 2,
                           border: '1px solid rgba(48, 146, 227, 0.2)'
                         }}
                       >
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <ImageIcon sx={{ color: "#3092e3", fontSize: '1rem' }} />
                           <Typography variant="caption" sx={{ color: "#333", fontWeight: "medium", fontSize: '0.75rem' }}>
                             {t("school_logo")}
                           </Typography>
                         </Box>
                         
                         {imageFile && (
                           <Box
                             component="img"
                             src={URL.createObjectURL(imageFile)}
                             alt="Logo Preview"
                             sx={{
                               width: 25,
                               height: 25,
                               objectFit: 'contain',
                               borderRadius: 1
                             }}
                           />
                         )}
                        
                         <Button
                           variant="outlined"
                           component="label"
                           size="small"
                           startIcon={imageFile ? <CheckCircleIcon fontSize="small" color="success" /> : <CloudUploadIcon fontSize="small" />}
                           sx={{
                             color: '#3092e3',
                             borderColor: 'rgba(48, 146, 227, 0.5)',
                             fontSize: '0.7rem',
                             py: 0.5,
                             px: 1,
                             '&:hover': {
                               borderColor: '#3092e3',
                               backgroundColor: 'rgba(48, 146, 227, 0.1)'
                             }
                           }}
                         >
                           {imageFile ? t("logo_selected") : t("change_logo")}
                           <input
                             type="file"
                             hidden
                             accept="image/*"
                             onChange={handleImageChange}
                           />
                         </Button>
                       </Box>
                     </Grid>

                     {/* School Image */}
                     <Grid item xs={12} md={6}>
                       <Box
                         sx={{
                           p: 1.5,
                           height: '70px',
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'center',
                           justifyContent: 'center',
                           gap: 0.5,
                           bgcolor: 'rgba(48, 146, 227, 0.05)',
                           borderRadius: 2,
                           border: '1px solid rgba(48, 146, 227, 0.2)'
                         }}
                       >
                         <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                           <ImageIcon sx={{ color: "#3092e3", fontSize: '1rem' }} />
                           <Typography variant="caption" sx={{ color: "#333", fontWeight: "medium", fontSize: '0.75rem' }}>
                             {t("school_photo")}
                           </Typography>
                         </Box>
                         
                         {schoolImageFile && (
                           <Box
                             component="img"
                             src={URL.createObjectURL(schoolImageFile)}
                             alt="Photo Preview"
                             sx={{
                               width: 25,
                               height: 25,
                               objectFit: 'contain',
                               borderRadius: 1
                             }}
                           />
                         )}
                        
                         <Button
                           variant="outlined"
                           component="label"
                           size="small"
                           startIcon={schoolImageFile ? <CheckCircleIcon fontSize="small" color="success" /> : <CloudUploadIcon fontSize="small" />}
                           sx={{
                             color: '#3092e3',
                             borderColor: 'rgba(48, 146, 227, 0.5)',
                             fontSize: '0.7rem',
                             py: 0.5,
                             px: 1,
                             '&:hover': {
                               borderColor: '#3092e3',
                               backgroundColor: 'rgba(48, 146, 227, 0.1)'
                             }
                           }}
                         >
                           {schoolImageFile ? t("photo_selected") : t("change_photo")}
                           <input
                             type="file"
                             hidden
                             accept="image/*"
                             onChange={handleSchoolImageChange}
                           />
                         </Button>
                       </Box>
                     </Grid>
                   </Grid>

                   {/* Update Button */}
                   {hasImageChanges && (
                     <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                       <Button
                         variant="contained"
                         onClick={handleUpdateImages}
                         disabled={updateSchoolMutation.isLoading}
                         sx={{
                           px: 2,
                           py: 0.5,
                           fontSize: '0.8rem',
                           fontWeight: 'bold',
                           borderRadius: 2,
                           bgcolor: '#3092e3',
                           color: '#fff',
                           '&:hover': {
                             bgcolor: '#1976d2',
                             transform: 'translateY(-1px)',
                           }
                         }}
                       >
                         {updateSchoolMutation.isLoading ? (
                           <CircularProgress size={14} sx={{ color: '#fff' }} />
                         ) : (
                           t("update_images")
                         )}
                       </Button>
                     </Box>
                   )}
                 </Box>

               </>
             )}
          </Box>
        </Box>
        )}

        {/* Important Links - right as sidebar accordion (full width for students) */}
        <Box
          sx={{
            flex: 1,
            minWidth: { xs: "100%", md: isStudent ? "100%" : 400 },
            maxWidth: { xs: "100%", md: isStudent ? "100%" : "50%" },
            background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.95) 100%)",
            borderRadius: 4,
            boxShadow: "0 8px 32px rgba(1, 104, 234, 0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            display: "flex",
            flexDirection: "column",
            p: { xs: 2, md: 3 },
            height: "100%",
            animation: "fadeInRight 1s ease-out 0.8s both",
            "&:hover": {
              transform: "translateY(-5px)",
              boxShadow: "0 12px 40px rgba(1, 104, 234, 0.2)",
            },
            transition: "all 0.3s ease",
            "@keyframes fadeInRight": {
              "0%": {
                opacity: 0,
                transform: "translateX(30px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateX(0)",
              },
            },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: "#333",
              fontWeight: "bold",
              mb: 3,
              fontSize: { xs: "1.4rem", md: "1.8rem" },
              letterSpacing: 0.5,
              textAlign: "center",
              flexShrink: 0,
            }}
          >
            🔗 {t("important_links")}
          </Typography>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
          {importantLinks.map((section, idx) => (
            <Box
              key={idx}
              sx={{
                mb: 1,
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "rgba(48, 146, 227, 0.05)",
              }}
            >
              <Box
                onClick={() => handleMenuClick(idx)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  cursor: "pointer",
                  px: 2,
                  py: 1.5,
                  bgcolor:
                    openMenu === idx ? section.color : "rgba(48, 146, 227, 0.08)",
                  color: "#333",
                  fontWeight: 700,
                  fontSize: 18,
                  transition: "all 0.3s ease-in-out",
                  boxShadow:
                    openMenu === idx ? "0 2px 12px rgba(1, 104, 234, 0.1)" : "none",
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: "1.7rem", color: "#3092e3" }}>
                    {section.icon}
                  </span>{" "}
                  {section.label}
                </span>
                {openMenu === idx ? <ExpandLessIcon sx={{ color: "#333" }} /> : <ExpandMoreIcon sx={{ color: "#333" }} />}
              </Box>
              {/* Submenu */}
              <Collapse in={openMenu === idx} timeout={300} unmountOnExit>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: "rgba(48, 146, 227, 0.03)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                    transition: "all 0.3s ease-in-out",
                    maxHeight: "300px",
                    overflowY: "auto",
                    overflowX: "hidden",
                    "&::-webkit-scrollbar": {
                      width: "8px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "rgba(48, 146, 227, 0.05)",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(48, 146, 227, 0.3)",
                      borderRadius: "4px",
                      "&:hover": {
                        background: "rgba(48, 146, 227, 0.5)",
                      },
                    },
                  }}
                >
                  {section.links.map((link, lidx) => (
                    <Button
                      key={lidx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        justifyContent: "flex-start",
                        textAlign: "left",
                        color: "#1976d2",
                        background: "#fff",
                        border: "1.5px solid #3092e3",
                        borderRadius: 2,
                        fontWeight: 600,
                        fontSize: 14,
                        px: 2,
                        py: 1,
                        mb: 0.5,
                        boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                        whiteSpace: "normal",
                        wordBreak: "break-all",
                        overflowWrap: "anywhere",
                        maxWidth: "100%",
                        width: "100%",
                        "&:hover": {
                          bgcolor: "#3092e3",
                          color: "#fff",
                          border: "1.5px solid #1976d2",
                        },
                      }}
                    >
                      {link.text}
                    </Button>
                  ))}
                </Box>
              </Collapse>
            </Box>
          ))}
          </Box>
        </Box>
      </Box>

      {/* Medium Management Dialog */}
      <Dialog
        open={mediumDialogOpen}
        onClose={() => setMediumDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LanguageIcon sx={{ color: "#3092e3" }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {t("medium_management")}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setMediumDialogOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#666', fontWeight: 600 }}>
              {t("current_mediums")}:
            </Typography>
            {mediums.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {mediums.map((medium) => (
                  <Chip
                    key={medium}
                    label={medium}
                    onDelete={() => handleRemoveMedium(medium)}
                    deleteIcon={<DeleteIcon />}
                    color="primary"
                    variant="outlined"
                    sx={{
                      fontSize: '0.9rem',
                      fontWeight: 600,
                    }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mb: 2, color: '#999', fontStyle: 'italic' }}>
                {t("no_mediums_added")}
              </Typography>
            )}
            
            <Autocomplete
              freeSolo
              options={[
                "HINDI",
                "ENGLISH",
                "MARATHI",
                "KANNADA",
                "TELUGU",
                "TAMIL",
                "GUJARATI",
                "ODIA",
                "PUNJABI",
                "BENGALI",
                "URDU"
              ].filter(option => !mediums.includes(option))}
              value={newMedium}
              onChange={(event, newValue) => {
                setNewMedium(newValue || "");
              }}
              onInputChange={(event, newInputValue) => {
                setNewMedium(newInputValue.toUpperCase());
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("add_medium")}
                  placeholder={t("type_or_select_medium")}
                  variant="outlined"
                  size="small"
                  sx={{ backgroundColor: '#f8f9fb' }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddMedium();
                    }
                  }}
                />
              )}
              sx={{ mb: 2 }}
            />
            
            <Button
              variant="outlined"
              onClick={handleAddMedium}
              disabled={!newMedium.trim() || mediums.includes(newMedium.trim().toUpperCase())}
              startIcon={<LanguageIcon />}
              sx={{
                color: '#3092e3',
                borderColor: '#3092e3',
                '&:hover': {
                  borderColor: '#1565c0',
                  backgroundColor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              {t("add_medium")}
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setMediumDialogOpen(false)}
            variant="outlined"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={() => {
              handleUpdateMediums();
              setMediumDialogOpen(false);
            }}
            variant="contained"
            disabled={!hasMediumChanges || updateSchoolMutation.isLoading}
            sx={{
              bgcolor: '#3092e3',
              '&:hover': {
                bgcolor: '#1976d2',
              },
              '&:disabled': {
                bgcolor: '#ccc',
                color: '#666',
              }
            }}
          >
            {updateSchoolMutation.isLoading ? (
              <CircularProgress size={20} sx={{ color: 'white' }} />
            ) : (
              t("update_mediums")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success and Error Notifications - only for SCHOOL_ADMIN users */}
      {isSchoolAdmin && (
        <>
          <Snackbar
            open={showSuccessMessage}
            autoHideDuration={5000}
            onClose={() => setShowSuccessMessage(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setShowSuccessMessage(false)} 
              severity="success" 
              sx={{ width: '100%' }}
            >
              {t("school_images_updated_successfully")}
            </Alert>
          </Snackbar>

          <Snackbar
            open={showErrorMessage}
            autoHideDuration={5000}
            onClose={() => setShowErrorMessage(false)}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert 
              onClose={() => setShowErrorMessage(false)} 
              severity="error" 
              sx={{ width: '100%' }}
            >
              {t("failed_to_update_school_images")}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
};

export default Dashboard;
